export type ReferenceResolutionPolicy = "case-insensitive" | "exact-case";

export interface FindReferenceMentionsOptions {
    readonly decodeMarkdownEscapes?: boolean;
}

export interface ReferenceMention {
    readonly typedTarget: string;
    readonly start: number;
    readonly end: number;
}

export interface ReferenceMentionTarget {
    readonly identity: string;
    readonly name: string;
}

export interface ResolvedReferenceMention extends ReferenceMention {
    readonly resolvedIdentity: string | null;
}

export interface ReferenceMentionSource {
    readonly identity: string;
    readonly text: string;
}

export interface ReferenceMentionOccurrence extends ResolvedReferenceMention {
    readonly sourceIdentity: string;
}

export interface BrokenReferenceTarget {
    readonly sourceIdentity: string;
    readonly typedTarget: string;
}

const TERMINAL_REFERENCE_WORD_CHARACTER = /^[\p{L}\p{N}_]/u;
const BARE_REFERENCE_CHARACTER = /^[\p{L}\p{N}_.-]$/u;
const MARKDOWN_ESCAPABLE_REFERENCE_CHARACTER = /[`*~[\]_]/;
const HTML_REFERENCE_ENTITIES_BY_CHARACTER: Readonly<
    Record<string, readonly string[]>
> = {
    "&": ["&amp;"],
    '"': ["&quot;"],
    "'": ["&#39;", "&apos;"],
    "<": ["&lt;"],
    ">": ["&gt;"],
};
const HTML_REFERENCE_ENTITY_MATCHES = Object.entries(
    HTML_REFERENCE_ENTITIES_BY_CHARACTER
).flatMap(([character, entities]) =>
    entities.map((entity) => ({ character, entity }))
);

interface HtmlReferenceEntityMatch {
    readonly character: string;
    readonly length: number;
}

const getHtmlReferenceEntity = (
    text: string,
    start: number
): HtmlReferenceEntityMatch | null => {
    const match = HTML_REFERENCE_ENTITY_MATCHES.find(
        ({ entity }) =>
            text.slice(start, start + entity.length).toLowerCase() === entity
    );
    return match
        ? { character: match.character, length: match.entity.length }
        : null;
};

const isInsideHtmlReferenceEntity = (text: string, end: number): boolean => {
    const entityStart = text.lastIndexOf("&", end - 1);
    if (entityStart === -1) return false;
    const match = getHtmlReferenceEntity(text, entityStart);
    return match !== null && end < entityStart + match.length;
};

const getMarkdownEscapedReferenceCharacter = (
    text: string,
    start: number
): string | null => {
    const character = text[start + 1];
    return text[start] === "\\" &&
        character !== undefined &&
        MARKDOWN_ESCAPABLE_REFERENCE_CHARACTER.test(character)
        ? character
        : null;
};

const hasTerminalBoundary = (text: string, end: number): boolean =>
    end === text.length ||
    (getMarkdownEscapedReferenceCharacter(text, end) === null &&
        getHtmlReferenceEntity(text, end) === null &&
        !TERMINAL_REFERENCE_WORD_CHARACTER.test(text.slice(end)));

interface CandidateMatch {
    readonly end: number;
    readonly typedTarget: string;
    readonly serializedCharacterCount: number;
}

type CandidateEndCondition = "terminal" | "continuation";

const matchesCandidateEndCondition = (
    text: string,
    end: number,
    condition: CandidateEndCondition
): boolean =>
    condition === "terminal"
        ? hasTerminalBoundary(text, end)
        : !isInsideHtmlReferenceEntity(text, end) &&
          (TERMINAL_REFERENCE_WORD_CHARACTER.test(text.slice(end)) ||
              getMarkdownEscapedReferenceCharacter(text, end) !== null ||
              getHtmlReferenceEntity(text, end) !== null);

interface RankedCandidateMatch extends CandidateMatch {
    readonly candidateLength: number;
    readonly order: number;
}

const isPreferredCandidateMatch = (
    current: RankedCandidateMatch | undefined,
    candidate: RankedCandidateMatch
): boolean =>
    current === undefined ||
    candidate.serializedCharacterCount > current.serializedCharacterCount ||
    (candidate.serializedCharacterCount === current.serializedCharacterCount &&
        (candidate.candidateLength > current.candidateLength ||
            (candidate.candidateLength === current.candidateLength &&
                candidate.order < current.order)));

interface PreparedReferenceCandidate {
    readonly candidate: string;
    readonly lowercaseCandidate: string;
    readonly order: number;
}

const preparedVocabularyCache = new WeakMap<
    readonly string[],
    readonly PreparedReferenceCandidate[]
>();

const prepareVocabulary = (
    vocabulary: readonly string[]
): readonly PreparedReferenceCandidate[] => {
    const cached = preparedVocabularyCache.get(vocabulary);
    if (cached) return cached;
    const prepared = vocabulary
        .filter((candidate) => candidate.length > 0)
        .map((candidate, order) => ({
            candidate,
            lowercaseCandidate: candidate.toLowerCase(),
            order,
        }))
        .sort(
            (left, right) =>
                right.candidate.length - left.candidate.length ||
                left.order - right.order
        );
    preparedVocabularyCache.set(vocabulary, prepared);
    return prepared;
};

const canMatchCandidate = (
    text: string,
    start: number,
    candidate: string,
    lowercaseCandidate: string,
    decodeMarkdownEscapes: boolean
): boolean => {
    if (candidate.length > text.length - start) return false;
    const candidateCharacter = lowercaseCandidate[0];
    const textCharacter = text[start];
    return (
        textCharacter?.toLowerCase() === candidateCharacter ||
        (decodeMarkdownEscapes &&
            textCharacter === "\\" &&
            MARKDOWN_ESCAPABLE_REFERENCE_CHARACTER.test(text[start + 1]) &&
            text[start + 1]?.toLowerCase() === candidateCharacter) ||
        textCharacter === "&"
    );
};

const matchLiteralCandidate = (
    text: string,
    start: number,
    candidate: string,
    lowercaseCandidate: string,
    endCondition: CandidateEndCondition
): CandidateMatch | null => {
    const end = start + candidate.length;
    const typedTarget = text.slice(start, end);
    return typedTarget.toLowerCase() === lowercaseCandidate &&
        matchesCandidateEndCondition(text, end, endCondition)
        ? { end, typedTarget, serializedCharacterCount: 0 }
        : null;
};

const hasEncodedCharacter = (
    text: string,
    start: number,
    maximumCandidateLength: number,
    decodeMarkdownEscapes: boolean
): boolean =>
    (decodeMarkdownEscapes ? /[\\&]/ : /&/).test(
        text.slice(start, start + maximumCandidateLength * 6)
    );

const matchCandidate = (
    text: string,
    start: number,
    candidate: string,
    decodeMarkdownEscapes: boolean,
    endCondition: CandidateEndCondition
): CandidateMatch | null => {
    const memo = new Map<string, CandidateMatch | null>();
    const visit = (
        textIndex: number,
        candidateIndex: number
    ): CandidateMatch | null => {
        const key = `${textIndex}:${candidateIndex}`;
        if (memo.has(key)) return memo.get(key) ?? null;
        if (candidateIndex === candidate.length) {
            const result = matchesCandidateEndCondition(
                text,
                textIndex,
                endCondition
            )
                ? {
                      end: textIndex,
                      typedTarget: "",
                      serializedCharacterCount: 0,
                  }
                : null;
            memo.set(key, result);
            return result;
        }

        const candidateCharacter = candidate[candidateIndex];
        const textCharacter = text[textIndex];
        if (textCharacter?.toLowerCase() === candidateCharacter.toLowerCase()) {
            const suffix = visit(textIndex + 1, candidateIndex + 1);
            if (suffix) {
                const result = {
                    ...suffix,
                    typedTarget: textCharacter + suffix.typedTarget,
                };
                memo.set(key, result);
                return result;
            }
        }

        if (
            decodeMarkdownEscapes &&
            textCharacter === "\\" &&
            MARKDOWN_ESCAPABLE_REFERENCE_CHARACTER.test(text[textIndex + 1]) &&
            text[textIndex + 1]?.toLowerCase() ===
                candidateCharacter.toLowerCase()
        ) {
            const suffix = visit(textIndex + 2, candidateIndex + 1);
            if (suffix) {
                const result = {
                    ...suffix,
                    typedTarget: text[textIndex + 1] + suffix.typedTarget,
                    serializedCharacterCount:
                        suffix.serializedCharacterCount + 1,
                };
                memo.set(key, result);
                return result;
            }
        }

        for (const entity of HTML_REFERENCE_ENTITIES_BY_CHARACTER[
            candidateCharacter
        ] ?? []) {
            if (
                text
                    .slice(textIndex, textIndex + entity.length)
                    .toLowerCase() !== entity
            ) {
                continue;
            }
            const suffix = visit(textIndex + entity.length, candidateIndex + 1);
            if (suffix) {
                const result = {
                    ...suffix,
                    typedTarget: candidateCharacter + suffix.typedTarget,
                    serializedCharacterCount:
                        suffix.serializedCharacterCount + 1,
                };
                memo.set(key, result);
                return result;
            }
        }

        memo.set(key, null);
        return null;
    };

    return visit(start, 0);
};

const matchBareReferenceTarget = (
    text: string,
    start: number,
    decodeMarkdownEscapes: boolean,
    prefix = "",
    decodeHtmlEntities = false
): Pick<CandidateMatch, "end" | "typedTarget"> | null => {
    let end = start;
    const characters = [prefix];
    while (end < text.length) {
        const escapedCharacter = decodeMarkdownEscapes
            ? getMarkdownEscapedReferenceCharacter(text, end)
            : null;
        if (escapedCharacter !== null) {
            characters.push(escapedCharacter);
            end += 2;
            continue;
        }
        const entity = decodeHtmlEntities
            ? getHtmlReferenceEntity(text, end)
            : null;
        if (entity !== null) {
            characters.push(entity.character);
            end += entity.length;
            continue;
        }
        const codePoint = text.codePointAt(end);
        if (codePoint === undefined) break;
        const character = String.fromCodePoint(codePoint);
        if (!BARE_REFERENCE_CHARACTER.test(character)) break;
        characters.push(character);
        end += character.length;
    }
    const typedTarget = characters.join("");
    return typedTarget.length > 0 ? { end, typedTarget } : null;
};

export function findReferenceMentions(
    text: string,
    vocabulary: readonly string[],
    { decodeMarkdownEscapes = true }: FindReferenceMentionsOptions = {}
): readonly ReferenceMention[] {
    if (!text.includes("@")) return [];
    const sortedVocabulary = prepareVocabulary(vocabulary);
    const maximumCandidateLength = sortedVocabulary[0]?.candidate.length ?? 0;
    const mentions: ReferenceMention[] = [];
    let cursor = 0;

    while (cursor < text.length) {
        const start = text.indexOf("@", cursor);
        if (start === -1) break;

        const targetStart = start + 1;
        const hasEncodedCandidate = hasEncodedCharacter(
            text,
            targetStart,
            maximumCandidateLength,
            decodeMarkdownEscapes
        );
        let vocabularyMatch: RankedCandidateMatch | undefined;
        let rejectedVocabularyPrefix: RankedCandidateMatch | undefined;
        for (const {
            candidate,
            lowercaseCandidate,
            order,
        } of sortedVocabulary) {
            if (
                !canMatchCandidate(
                    text,
                    targetStart,
                    candidate,
                    lowercaseCandidate,
                    decodeMarkdownEscapes
                )
            ) {
                continue;
            }
            const match =
                matchLiteralCandidate(
                    text,
                    targetStart,
                    candidate,
                    lowercaseCandidate,
                    "terminal"
                ) ??
                (hasEncodedCandidate
                    ? matchCandidate(
                          text,
                          targetStart,
                          candidate,
                          decodeMarkdownEscapes,
                          "terminal"
                      )
                    : null);
            if (!match) {
                const rejectedMatch =
                    matchLiteralCandidate(
                        text,
                        targetStart,
                        candidate,
                        lowercaseCandidate,
                        "continuation"
                    ) ??
                    (hasEncodedCandidate
                        ? matchCandidate(
                              text,
                              targetStart,
                              candidate,
                              decodeMarkdownEscapes,
                              "continuation"
                          )
                        : null);
                if (rejectedMatch) {
                    const rankedRejectedMatch = {
                        ...rejectedMatch,
                        candidateLength: candidate.length,
                        order,
                    };
                    if (
                        isPreferredCandidateMatch(
                            rejectedVocabularyPrefix,
                            rankedRejectedMatch
                        )
                    ) {
                        rejectedVocabularyPrefix = rankedRejectedMatch;
                    }
                }
                continue;
            }
            const rankedMatch = {
                ...match,
                candidateLength: candidate.length,
                order,
            };
            if (!hasEncodedCandidate) {
                vocabularyMatch = rankedMatch;
                break;
            }
            if (isPreferredCandidateMatch(vocabularyMatch, rankedMatch)) {
                vocabularyMatch = rankedMatch;
            }
        }
        const fallbackPrefix =
            rejectedVocabularyPrefix &&
            (vocabularyMatch === undefined ||
                rejectedVocabularyPrefix.end > vocabularyMatch.end)
                ? rejectedVocabularyPrefix
                : undefined;
        const bareMatch = fallbackPrefix
            ? matchBareReferenceTarget(
                  text,
                  fallbackPrefix.end,
                  decodeMarkdownEscapes,
                  fallbackPrefix.typedTarget,
                  true
              )
            : vocabularyMatch
              ? null
              : matchBareReferenceTarget(
                    text,
                    targetStart,
                    decodeMarkdownEscapes
                );
        const typedTarget =
            (fallbackPrefix ? undefined : vocabularyMatch?.typedTarget) ??
            bareMatch?.typedTarget ??
            "";

        if (typedTarget.length === 0) {
            cursor = targetStart;
            continue;
        }

        const end =
            (fallbackPrefix ? undefined : vocabularyMatch?.end) ??
            bareMatch?.end ??
            targetStart;
        mentions.push({ typedTarget, start, end });
        cursor = end;
    }

    return mentions;
}

export function resolveReferenceMention(
    mention: ReferenceMention,
    targets: readonly ReferenceMentionTarget[],
    policy: ReferenceResolutionPolicy
): ResolvedReferenceMention {
    const typedTarget =
        policy === "case-insensitive"
            ? mention.typedTarget.toLowerCase()
            : mention.typedTarget;
    const target = targets.find((candidate) => {
        const name =
            policy === "case-insensitive"
                ? candidate.name.toLowerCase()
                : candidate.name;
        return name === typedTarget;
    });

    return {
        ...mention,
        resolvedIdentity: target?.identity ?? null,
    };
}

export function collectReferenceMentionOccurrences(
    sources: readonly ReferenceMentionSource[],
    vocabulary: readonly string[],
    targets: readonly ReferenceMentionTarget[],
    policy: ReferenceResolutionPolicy
): readonly ReferenceMentionOccurrence[] {
    return sources.flatMap((source) =>
        findReferenceMentions(source.text, vocabulary).map((mention) => ({
            ...resolveReferenceMention(mention, targets, policy),
            sourceIdentity: source.identity,
        }))
    );
}

export function getBrokenReferenceTargets(
    occurrences: readonly ReferenceMentionOccurrence[]
): readonly BrokenReferenceTarget[] {
    const seenBySource = new Map<string, Set<string>>();
    const broken: BrokenReferenceTarget[] = [];

    for (const occurrence of occurrences) {
        if (occurrence.resolvedIdentity !== null) continue;
        const seenTargets = seenBySource.get(occurrence.sourceIdentity);
        if (seenTargets?.has(occurrence.typedTarget)) continue;

        if (seenTargets) {
            seenTargets.add(occurrence.typedTarget);
        } else {
            seenBySource.set(
                occurrence.sourceIdentity,
                new Set([occurrence.typedTarget])
            );
        }
        broken.push({
            sourceIdentity: occurrence.sourceIdentity,
            typedTarget: occurrence.typedTarget,
        });
    }

    return broken;
}
